import { memo, useEffect, useState } from 'react';
import { animate, useReducedMotion } from 'framer-motion';

/**
 * TallyCounter — the animated vote-tally readout for the Stage lynch reveal.
 *
 * One row per candidate: name, a bar that fills to their share of the vote, and a
 * numeric count-up. The count-up uses framer-motion's imperative `animate(0, n)`
 * so the number ticks in tabular-nums Inter. The condemned row (`condemnedId`)
 * fills blood; every other row fills gold.
 *
 * `durationMs` is fed by the sequencer's `tallyCountMs` param so the count-up
 * lands in sync with the room-wide reveal choreography. Reduced-motion renders the
 * final counts and full bars instantly (no ticking).
 */

export interface TallyRow {
  /** Candidate id (stable key). */
  id: string;
  /** Display name. */
  name: string;
  /** Vote count this candidate received. */
  votes: number;
}

export interface TallyCounterProps {
  rows: TallyRow[];
  /** The condemned candidate's id → blood bar. Others are gold. */
  condemnedId?: string | null;
  /** Count-up duration in ms (sequencer tallyCountMs). Default 1600. */
  durationMs?: number;
}

function TallyCounterImpl({ rows, condemnedId = null, durationMs = 1600 }: TallyCounterProps) {
  const reduced = useReducedMotion();
  const maxVotes = Math.max(1, ...rows.map((r) => r.votes));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%' }}>
      {rows.map((row) => (
        <TallyBar
          key={row.id}
          row={row}
          maxVotes={maxVotes}
          condemned={row.id === condemnedId}
          durationMs={durationMs}
          reduced={!!reduced}
        />
      ))}
    </div>
  );
}

function TallyBar({
  row,
  maxVotes,
  condemned,
  durationMs,
  reduced,
}: {
  row: TallyRow;
  maxVotes: number;
  condemned: boolean;
  durationMs: number;
  reduced: boolean;
}) {
  const [display, setDisplay] = useState(reduced ? row.votes : 0);
  const fillPct = (row.votes / maxVotes) * 100;

  useEffect(() => {
    if (reduced) {
      setDisplay(row.votes);
      return;
    }
    // Count up 0 → votes. The effect deps already gate re-runs to a genuine
    // votes/duration change, so no manual "already animated" guard is needed —
    // and adding one would fight React StrictMode's mount→unmount→mount (the
    // remount would skip the animation and freeze the number at 0). On cleanup
    // we stop the tween AND snap to the final value, so an interrupted run
    // (StrictMode unmount, or votes changing mid-count) lands correct, never 0.
    setDisplay(0);
    const controls = animate(0, row.votes, {
      duration: durationMs / 1000,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => {
      controls.stop();
      setDisplay(row.votes);
    };
  }, [row.votes, durationMs, reduced]);

  const barColor = condemned
    ? 'linear-gradient(90deg, var(--blood-3, #802636), var(--blood-5, #c22f3e))'
    : 'linear-gradient(90deg, var(--gold-3, #a9762f), var(--gold-5, #e6b866))';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 6,
            fontFamily: 'var(--font-ui, Inter, sans-serif)',
            fontSize: 20,
            color: condemned ? 'var(--blood-6, #ff5a4f)' : 'var(--text-primary, #fff)',
            fontWeight: condemned ? 600 : 500,
          }}
        >
          <span
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {row.name}
          </span>
        </div>
        {/* Bar track + fill */}
        <div
          style={{
            height: 12,
            borderRadius: 'var(--radius-pill, 48px)',
            background: 'var(--ink-3, #241619)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${fillPct}%`,
              background: barColor,
              borderRadius: 'var(--radius-pill, 48px)',
              transition: reduced
                ? 'none'
                : `width ${durationMs}ms var(--ease-standard)`,
            }}
          />
        </div>
      </div>
      <div
        style={{
          fontFamily: 'var(--font-ui, Inter, sans-serif)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 28,
          fontWeight: 700,
          minWidth: 44,
          textAlign: 'right',
          color: condemned ? 'var(--blood-6, #ff5a4f)' : 'var(--gold-5, #e6b866)',
        }}
      >
        {display}
      </div>
    </div>
  );
}

export const TallyCounter = memo(TallyCounterImpl);
